// =============================================================
// CALENDAR HELPERS
// =============================================================
// Turn a drop (Event) into something a user can add to their calendar or copy
// into one manually:
//   • buildIcsFile()        → downloads a standards-compliant .ics file
//   • googleCalendarUrl()   → a "add to Google Calendar" link
//   • dropDetailsText()     → all drop info as plain text for copy/paste
//
// Drop dates are stored as "YYYY-MM-DD" and times as free-form strings like
// "12:00 - 14:00". We parse the start (and optional end) out of the time string
// and fall back to a sensible 2-hour window when only a start is given.
// =============================================================

import type { Event } from './store'

const mapsUrl = (address: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`

/** Pull "HH:MM" pairs out of a free-form time string (e.g. "12:00 - 14:00"). */
const parseTimes = (time: string): { start?: string; end?: string } => {
  const matches = (time || '').match(/\d{1,2}:\d{2}/g) || []
  return { start: matches[0], end: matches[1] }
}

/**
 * Resolve a drop's start/end as JS Dates. When no end is given we default to a
 * 2-hour window; when no valid time is given we default to a 12:00 start.
 */
const resolveDates = (event: Event): { start: Date; end: Date } => {
  const { start, end } = parseTimes(event.time)
  const [sh, sm] = (start || '12:00').split(':').map(Number)
  const startDate = new Date(`${event.date}T00:00:00`)
  startDate.setHours(sh || 12, sm || 0, 0, 0)

  const endDate = new Date(startDate)
  if (end) {
    const [eh, em] = end.split(':').map(Number)
    endDate.setHours(eh || sh + 2, em || 0, 0, 0)
  } else {
    endDate.setHours(startDate.getHours() + 2)
  }
  return { start: startDate, end: endDate }
}

/** Format a Date as the local floating timestamp .ics / Google expect. */
const fmt = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `T${p(d.getHours())}${p(d.getMinutes())}00`
  )
}

const eventTitle = (event: Event) => `🎣 ${event.title} — ${event.brand}`

const eventDescription = (event: Event) => {
  const lines = [event.description || '']
  if (event.products?.length) {
    lines.push('', 'In the box:')
    event.products.forEach((p) => lines.push(`• ${p.name} (${p.brand})`))
  }
  lines.push('', `Directions: ${mapsUrl(event.address)}`)
  return lines.join('\n').trim()
}

/** Plain-text summary of a drop, for "copy all info" into any calendar. */
export const dropDetailsText = (event: Event): string => {
  const lines = [
    `🎣 ${event.title}`,
    `Brand: ${event.brand}`,
    `Date: ${event.date}`,
    `Time: ${event.time}`,
    `Location: ${event.address}`,
    `Directions: ${mapsUrl(event.address)}`,
  ]
  if (event.products?.length) {
    lines.push('', 'In the box:')
    event.products.forEach((p) => lines.push(`• ${p.name} (${p.brand})`))
  }
  return lines.join('\n')
}

/** "Add to Google Calendar" URL with the drop prefilled. */
export const googleCalendarUrl = (event: Event): string => {
  const { start, end } = resolveDates(event)
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: eventTitle(event),
    dates: `${fmt(start)}/${fmt(end)}`,
    details: eventDescription(event),
    location: event.address,
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/** Build and trigger a download of an .ics file for the drop. */
export const downloadIcs = (event: Event): void => {
  const { start, end } = resolveDates(event)
  const esc = (s: string) =>
    s
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;')
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Finns i Sjon//Drop//EN',
    'BEGIN:VEVENT',
    `UID:${event.id}@finnsisjon`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${esc(eventTitle(event))}`,
    `DESCRIPTION:${esc(eventDescription(event))}`,
    `LOCATION:${esc(event.address)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${event.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
